import React from 'react';
import classNames from 'classnames';
import {DirectoryAudioItem} from 'features/Provider/Experiences/WellniteAudios/Library/DirectoryAudioItemV2';
import {Audio} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

// Add this constant at the top of the file, outside the component
const SKELETON_ITEMS = [
  'audio-skeleton-1',
  'audio-skeleton-2',
  'audio-skeleton-3',
];

interface AudioListProps {
  title: string;
  description?: string;
  audios: Audio[];
  handlePlay: (id: string) => void;
  handleAudioClick: (id: string) => void;
  currentlyPlayingId: string | null;
  isLoadingAudios: boolean;
  max?: number;
  classNameList?: string;
  showViewAll?: boolean;
}

export function AudioList({
  title,
  description,
  audios,
  handlePlay,
  handleAudioClick,
  currentlyPlayingId,
  isLoadingAudios,
  max = 3,
  classNameList,
  showViewAll = false,
}: AudioListProps): React.ReactElement {
  const {t} = useTranslation();
  return (
    <section className="text-left @container mb-12">
      <div className="flex justify-between items-center">
        <div className="py-4">
          {description && <p>{description}</p>}
          <h2 className="text-[22px] font-montserrat font-semibold">{title}</h2>
        </div>
        {showViewAll && audios?.length > max ? (
          <Link
            className="font-semibold font-inter cursor-pointer text-sm"
            to={`/provider/audios/${title}`}
          >
            {t('wellniteContent.viewAll', 'View All')}
          </Link>
        ) : null}
      </div>

      {!isLoadingAudios ? (
        <div
          className={classNames(
            `flex overflow-x-auto`,
            !audios.length && 'justify-center items-center',
            classNameList,
          )}
        >
          {audios.length ? (
            audios
              ?.slice(0, max)
              ?.map(audio => (
                <DirectoryAudioItem
                  key={audio._id}
                  {...audio}
                  isPlaying={currentlyPlayingId === audio._id}
                  onPlay={handlePlay}
                  onClick={handleAudioClick}
                />
              ))
          ) : (
            <h3 className="col-span-full flex justify-center items-center min-h-[100px]">
              {t('wellniteContent.noAvailableAudios', 'No audio available')}
            </h3>
          )}
        </div>
      ) : (
        <AudioSkeletonLoader />
      )}
    </section>
  );
}

const AudioSkeletonLoader = () => {
  return (
    <div className=" grid grid-cols-3 gap-4">
      {SKELETON_ITEMS.map(item => (
        <div key={item} className="flex flex-col items-center space-y-4">
          <div className="w-full h-[110px] bg-gray-300 rounded-xl animate-pulse"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
};
